// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  getFulfillmentBySkuReport,
  serializeFulfillmentBySkuReportFilter,
} from '@/report/api/get-fulfillment-by-sku-report';
import { FulfillmentBySkuReportFilter } from '@/report/api/types';
import { ONE_MINUTE_MS } from '@/utilities/time-constants';
import { useSuspenseQuery } from '@tanstack/react-query';

interface UseFulfillmentBySkuReportOptions {
  filter?: FulfillmentBySkuReportFilter;
}

/**
 * Base query key for the org-wide fulfillment-by-SKU report. Invalidating this
 * key refreshes the report for every filter combination, so callers that change
 * fulfillment coverage (e.g. creating a fulfillment order) can keep it current.
 */
export const fulfillmentBySkuReportBaseQueryKey = [
  'report',
  'fulfillment-by-sku',
] as const;

export function useFulfillmentBySkuReport(
  options: UseFulfillmentBySkuReportOptions = {}
) {
  const filterString = serializeFulfillmentBySkuReportFilter(options.filter);
  const { data, ...query } = useSuspenseQuery({
    queryKey: [...fulfillmentBySkuReportBaseQueryKey, filterString],
    queryFn: () => getFulfillmentBySkuReport(options.filter),
    refetchOnWindowFocus: true,
    staleTime: ONE_MINUTE_MS,
  });

  return {
    rows: data.rows,
    totalCount: data.totalCount,
    ...query,
  };
}
